import type { MowerSpec } from '../sim/mower';

/** Content-side mower definition: the sim spec plus progression data. */
export interface MowerDef extends MowerSpec {
  /** Progression rank used by `minTier` level rules (0 = starter). */
  tier: number;
  /** One-line handling summary shown before a level starts. */
  blurb: string;
  /** Campaign level that must be cleared before it can be bought (0 = owned from the start). */
  unlockLevel: number;
  /** Coin price once unlocked. */
  price: number;
  /** Handheld tools it can carry: walk-behind mowers leave one hand free, riding mowers two. */
  toolSlots: number;
}

/**
 * Prototype mower roster (values are starting points for playtest tuning). The full roster with
 * cut widths, fuel and tool slots arrives in Phase 3 (docs/GDD.md §4).
 */
export const MOWERS = {
  reel: {
    id: 'reel',
    toolSlots: 1,
    unlockLevel: 0,
    price: 0,
    breakPower: 0,
    cutPower: 1,
    bite: 1,
    blurb: 'Swipe back to pull back.',
    name: 'Reel Mower',
    tier: 0,
    ticksPerTile: 30,
    maxHp: 1,
    turnRule: { kind: 'free90Pullback', cooldownTicks: 180 },
  },
  push: {
    id: 'push',
    toolSlots: 1,
    unlockLevel: 5,
    price: 40,
    fuelTiles: 220,
    breakPower: 1,
    cutPower: 2,
    bite: 2,
    blurb: 'Quarter turns. No reverse.',
    name: 'Push Mower',
    tier: 1,
    ticksPerTile: 26,
    maxHp: 1,
    turnRule: { kind: 'free90' },
  },
  selfPropelled: {
    id: 'selfPropelled',
    toolSlots: 1,
    unlockLevel: 10,
    price: 180,
    breakPower: 1,
    cutPower: 2,
    bite: 2,
    fuelTiles: 240,
    blurb: 'Has a SLOW gear (top bar).',
    name: 'Self-Propelled',
    tier: 2,
    ticksPerTile: 24,
    throttle: { slowTicksPerTile: 38 },
    maxHp: 2,
    turnRule: { kind: 'free90' },
  },
  tractor: {
    id: 'tractor',
    toolSlots: 2,
    unlockLevel: 20,
    price: 250,
    fuelTiles: 320,
    breakPower: 3,
    cutPower: 3,
    bite: 3,
    blurb: 'Wide turns, one-pass cut.',
    name: 'Lawn Tractor',
    tier: 3,
    ticksPerTile: 20,
    maxHp: 3,
    turnRule: { kind: 'turnSpacing', sameDirectionMinTiles: 2 },
  },
  zeroTurn: {
    id: 'zeroTurn',
    toolSlots: 2,
    unlockLevel: 30,
    price: 450,
    fuelTiles: 260,
    breakPower: 2,
    cutPower: 3,
    bite: 2,
    blurb: 'Hold to spin on the spot.',
    name: 'Zero-Turn',
    tier: 4,
    ticksPerTile: 17,
    maxHp: 3,
    turnRule: { kind: 'freeSpin' },
  },
  fairway: {
    id: 'fairway',
    toolSlots: 2,
    unlockLevel: 61,
    price: 900,
    fuelTiles: 400,
    breakPower: 4,
    cutPower: 3,
    bite: 2,
    wings: true,
    blurb: 'Side reels mow 3 wide.',
    name: 'Fairway Mower',
    tier: 5,
    ticksPerTile: 22,
    maxHp: 3,
    turnRule: { kind: 'free90' },
  },
} as const satisfies Record<string, MowerDef>;

export type MowerId = keyof typeof MOWERS;

/** The roster widened to {@link MowerDef}, for code that reads optional fields like fuel. */
export const MOWER_DEFS: Readonly<Record<MowerId, MowerDef>> = MOWERS;

export const MOWER_IDS = Object.keys(MOWERS) as MowerId[];

export function isMowerId(value: unknown): value is MowerId {
  return typeof value === 'string' && value in MOWERS;
}
